<template>
  <Layout>
    <McLoading :isLoading="isSubmitting" />
    <AlertQuota :quotaStatus="quotaStatus" />
    <CreateRoErrorBanner v-if="isRoSwatActive" :errors="swatMobilityError" />

    <PageHeader :title="title">
      <template #afterTitle>
        <div class="d-flex align-items-center gap-2">
          <McButton
            variant="secondary"
            icon="icon-outline_close"
            :text="$t('global.action.cancel')"
            :disabled="isSubmitting"
            @click="handleCancel"
          />
          <McButton
            v-if="activeKey === 'set-parameters'"
            variant="secondary"
            :text="selectedContent.secondaryBtn.title"
            :icon="selectedContent.secondaryBtn.icon"
            :disabled="disableSecondaryBtn"
            @click="handleClickSecondaryBtn"
          />
          <McButton
            variant="primary"
            :text="selectedContent.primaryBtn.title"
            :icon="selectedContent.primaryBtn?.icon"
            :disabled="disablePrimaryBtn"
            @click="handleClickPrimaryBtn"
          />
        </div>
      </template>
    </PageHeader>

    <div
      :class="[
        'd-flex',
        'justify-content-between',
        'align-items-center',
      ]"
    >
      <McLinearWizard :activeKey="activeKey" :items="wizardItems" />
      <QuotaCounter />
    </div>

    <FormulateForm ref="form-ref" v-model="formValues">
      <AttachDelivery
        v-show="activeKey === 'attach-delivery'"
        ref="attach-delivery-ref"
        class="mt-3"
        :formValues="formValues"
        :fullObjects="fullObjects"
        :isReoptimize="isReoptimize"
        :orderReoptimzeIds="orderReoptimizeIds"
        :highPriorityOrdersIds="highPriorityOrdersIds"
        :isRoSwatActive="isRoSwatActive"
        @update:formValues="handleUpdateFormValues"
        @update:fullObjects="({ key, value }) => $set(fullObjects, key, value)"
      />
      <SetParameters
        v-show="activeKey === 'set-parameters'"
        ref="set-parameters-ref"
        class="mt-3"
        :formValues="formValues"
        :fullObjects="fullObjects"
        :isLoading="isSubmitting"
        :isReoptimize="isReoptimize"
        :vehicleReoptimizeIds="vehicleReoptimizeIds"
        :isVisible="activeKey === 'set-parameters'"
        @update:formValues="handleUpdateFormValues"
      />
    </FormulateForm>
    <BackToResult :roNumber="roNumber" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { Helper } from '@/mixin/helper'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McLinearWizard from '@/components/McLinearWizard'

import AttachDelivery from './contents/AttachDelivery'
import SetParameters from './contents/SetParameters'
import BackToResult from './components/Re-Optimize/Modal/BackToResult'
import QuotaCounter from './components/QuotaCounter'
import AlertQuota from './components/QuotaCounter/AlertQuota'
import CreateRoErrorBanner from './components/CreateRoErrorBanner'

import getWizardItems from './lib/constants/wizard-items.js'

import * as RO from '@/services/rest-api/lib/route-optimization'

import { errorMappings } from './lib/constants/error-mappings'
import { errorV2Mappings } from './lib/constants/error-v2-mappings'
import { FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT } from './lib/constants/default-values'
import { errorCodeToastMap } from './lib/constants/error-code-mappings'
import { EventBus } from './lib/event-bus.js'

import { cloneDeep, set } from 'lodash'
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'

moment.locale('id')

export default {
  page: {
    title: 'Route Optimization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McLinearWizard,
    AttachDelivery,
    SetParameters,
    BackToResult,
    QuotaCounter,
    AlertQuota,
    CreateRoErrorBanner,
  },
  mixins: [Helper],
  data() {
    return {
      isSubmitting: false,
      isOncePressed: false,
      activeKey: 'set-parameters',
      formValues: cloneDeep(FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT),
      fullObjects: {},

      orderReoptimizeIds: [],
      vehicleReoptimizeIds: [],

      highPriorityOrdersData: [],
      highPriorityOrdersIds: [],
      settingData: {},
      isChangedPriority: false,
      deliveryOrdersPriority: [],
      quotaStatus: 'available',
      isFetchingDO: false,
      swatMobilityError: null,
    }
  },
  computed: {
    ...mapState('routeOptimization', ['showResponse']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll(['tms-user-group:read']) && 
        this.isSuperUser()
      )
    },
    title() {
      return `${this.$t('routeOptimization.editTitle')} #${this.settingData.number || ''}`
    },
    wizardItems() {
      return getWizardItems(this)
    },
    contentItems() {
      return {
        'attach-delivery': {
          component: AttachDelivery,
          primaryBtn: {
            title: this.$t('global.next'),
            icon: 'icon-outline_arrow-right',
          },
        },
        'set-parameters': {
          component: SetParameters,
          secondaryBtn: {
            title: this.$t('global.prev'),
            icon: 'icon-outline_arrow-left',
          },
          primaryBtn: {
            title: this.$t('routeOptimization.action.reOptimize'),
            icon: 'icon-fill_flash',
          },
        },
      }
    },
    selectedContent() {
      return this.contentItems[this.activeKey]
    },
    disableSecondaryBtn() {
      return false || this.isSubmitting || this.isFetchingDO
    },
    disablePrimaryBtn() {
      if (this.isSubmitting || this.isOncePressed || this.isFetchingDO) return true

      if (this.activeKey === 'set-parameters') {
        const hasDeparture = !!this.formValues.expected_departure_on
        const hasValidServiceTime =
          moment.duration(this.formValues['options.service_time']).asMilliseconds() >=
          moment.duration('PT3M').asMilliseconds()
        const hasVehicles = this.formValues.vehicle_ids.length > 0
        return !(hasDeparture && hasValidServiceTime && hasVehicles)
      }

      const hasStartAddress = !!this.formValues.start_address_id
      const hasDeliveryOrders = this.formValues.delivery_order_ids.length > 0

      return !(hasStartAddress && hasDeliveryOrders)
    },
    roNumber() {
      return this.settingData.number
    },
    id() {
      return this.$route.params.id
    },
    isReoptimize() {
      return !!this.$route.params.id
    },
    isRoSwatActive() {
      const isSamator = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false
      
      return isSamator && isActive
    },
    originAddressIdRoSwat() {
      const originAddressId = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.origin_address_id || null
      return originAddressId
    },
  },
  created() {
    EventBus.$on('update-form-values', this.handleUpdateFormValues)
  },
  async mounted() {
    await this.initializeData()
  },
  beforeDestroy() {
    EventBus.$off('update-form-values', this.handleUpdateFormValues)
  },
  methods: {
    async initializeData() {
      await this.getDetailSetting()
      await Promise.all([this.getDOSetting(), this.getVehicleSetting()])
    },
    handleCancel() {
      this.$bvModal.show('modal-back-to-result')
    },

    handleClickSecondaryBtn() {
      if (this.activeKey === 'attach-delivery') return

      this.activeKey = 'attach-delivery'
    },

    handleClickPrimaryBtn() {
      if (this.activeKey === 'attach-delivery') {
        this.activeKey = 'set-parameters'

        return
      }

      this.handleSubmit()
    },

    handleUpdateFormValues({ key, value }) {
      if (key === 'delivery_orders') {
        this.isChangedPriority = true
      }
      this.$set(this.formValues, key, value)
    },

    async handleSubmit() {
      if (this.$refs['form-ref'].hasErrors) {
        this.$mcToast.error(this.$t('routeOptimization.alert.inputFormatError'))
        return
      }
      this.isOncePressed = true
      this.isSubmitting = true

      const data = {
        type: 'SINGLE_PICK-MULTI_DROP',
      }

      if (this.settingData.engine) {
        data.engine = this.settingData.engine
      }

      if (!this.isChangedPriority) {
        this.formValues.delivery_orders = this.deliveryOrdersPriority
      }

      for (const [key, value] of Object.entries(this.formValues)) {
        if (value === null || key === 'delivery_order_ids') continue
        
        if (key === 'user_group_id' && !this.hasTmsUserGroupAccess) continue

        if (key === 'user_group_id' && value) {
          set(data, key, Number(value))
        } else {
          set(data, key, value)
        }
      }

      const {
        start_address_id,
        delivery_order_ids,
        delivery_orders,
        vehicle_ids,
        user_group_id,
        ...configToSave
      } = this.formValues

      const userConfiguration = {
        ...configToSave,
        company_id: this.getCompanyId(),
        user_id: this.getUserId(),
      }

      const existingConfig = JSON.parse(localStorage.getItem('roConfiguration') || '{}')
      const currentUserId = this.getUserId()
      existingConfig[currentUserId] = userConfiguration
      localStorage.setItem('roConfiguration', JSON.stringify(existingConfig))

      const res = await RO.reOptimize(this.id, data)

      if (res.errors) {
        if (res.status === 504) {
          this.isSubmitting = false
          this.isOncePressed = false
          return false
        }
        const errors = res
        let errorMessage = this.$t('routeOptimization.alert.inputFormatError')
        if (errorMappings[errors.message]) {
          errorMessage = this.$t(errorMappings[errors.message])
        }

        if (this.isRoSwatActive) {
          this.swatMobilityError = res.errors
        }

        if (res.errors_v2 !== null) {
          // Check for error codes in errorCodeToastMap first
          const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
          if (issue) {
            errorMessage = this.$t(errorCodeToastMap[issue.code])
            if (issue.code === 'FLEET_TASK.ROUTE_OPTIMIZATION_LIMIT_REACHED') {
              this.quotaStatus = 'empty'
              this.$mcToast.error(errorMessage)
              this.isSubmitting = false
              this.isOncePressed = false
              return
            }
          } else if (
            res.errors_v2?.issues[0]?.code === 'FLEET_TASK.DELIVERY_ORDER_INVALID_STATUS'
          ) {
            // reset all values form
            this.activeKey = 'attach-delivery'
            this.formValues = cloneDeep(FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT)
            this.fullObjects = {}
            this.isOncePressed = false

            // reset on set parameters
            this.$refs['set-parameters-ref'].$refs['table-select-vehicle-ref'].ResetTableState()
            this.$refs['set-parameters-ref'].$refs['activity-duration-ref'].ResetFormValues()

            errorMessage = this.$t(errorV2Mappings[res.errors_v2?.issues[0]?.code])
          }
        }

        this.$mcToast.error(errorMessage)
        this.isSubmitting = false
        this.isOncePressed = false
        this.quotaStatus = 'available'
        return
      }

      this.isSubmitting = false
      this.$router.push({ name: 'Route Optimization Detail', params: { id: res.data.id } })
    },
    async getDetailSetting() {
      const res = await RO.showSetting(this.id)

      if (res.errors) return

      const setting = res.data

      this.formValues.start_address_id = setting.start_address.id
      this.fullObjects.start_address_id = setting.start_address

      if (setting.user_group && this.hasTmsUserGroupAccess) {
        this.formValues.user_group_id = setting.user_group.id
        this.fullObjects.user_group = setting.user_group
      }

      this.settingData = setting

      if (setting.expected_departure_on) {
        this.formValues.expected_departure_on = moment(setting.expected_departure_on).format(
          'YYYY-MM-DDTHH:mm:ss.SSSZ'
        )
      }

      if (setting.expected_arrival_on) {
        this.formValues.expected_end_on = moment(setting.expected_arrival_on).format(
          'YYYY-MM-DDTHH:mm:ss.SSSZ'
        )
      }

      if (setting.options) {
        this.mapOptionsToFormValues(setting.options, 'options')
      }
    },

    mapOptionsToFormValues(options, prefix = '') {
      Object.keys(options).forEach((optionKey) => {
        const optionValue = options[optionKey]
        const formKey = prefix ? `${prefix}.${optionKey}` : optionKey

        if (optionValue && typeof optionValue === 'object' && !Array.isArray(optionValue)) {
          this.mapOptionsToFormValues(optionValue, formKey)
        } else {
          if (Object.prototype.hasOwnProperty.call(this.formValues, formKey)) {
            this.formValues[formKey] = optionValue
          }
        }
      })
    },
    async getDOSetting() {
      try {
        this.isFetchingDO = true

        const params = {
          page: 1,
          limit: 100,
          filters: ['status.name in CONFIRMED RESCHEDULED'],
        }

        this.highPriorityOrdersData = []
        this.highPriorityOrdersIds = []

        const firstRes = await RO.getSettingDelivery(this.id)(params)

        if (firstRes.errors) return

        const firstDeliveryOrderData = firstRes.data.paginated_result.filter((order) => {
          if (this.hasTmsUserGroupAccess) {
            return order.user_group?.id == this.formValues.user_group_id
          }
          return true
        })

        const firstMetadata = firstRes.metadata

        this.processDeliveryOrderData(firstDeliveryOrderData)

        if (firstMetadata.total_page > 1) {
          for (let currentPage = 2; currentPage <= firstMetadata.total_page; currentPage++) {
            params.page = currentPage
            const res = await RO.getSettingDelivery(this.id)(params)

            if (res.errors) continue
            const nextDeliveryOrderData = res.data.paginated_result.filter((order) => {
              if (this.hasTmsUserGroupAccess) {
                return order.user_group?.id == this.formValues.user_group_id
              }
              return true
            })
            this.processDeliveryOrderData(nextDeliveryOrderData || res.data)
          }
        }
      } finally {
        this.isFetchingDO = false
      }
    },
    processDeliveryOrderData(deliveryOrders) {
      if (!Array.isArray(deliveryOrders)) {
        console.warn('deliveryOrders is not an array:', deliveryOrders)
        return
      }

      const highPriorityOrders = deliveryOrders.filter((order) => order.priority === 'high')

      const simplifiedDeliveryOrders = deliveryOrders.map((order) => ({
        id: order.id,
        priority: order.priority,
      }))

      const simplifiedHighPriority = highPriorityOrders.map((order) => ({
        id: order.id,
        priority: order.priority,
      }))

      const deliveryOrdersPriorityMap = new Map(
        this.deliveryOrdersPriority.map((order) => [String(order.id), order])
      )
      simplifiedDeliveryOrders.forEach((order) => {
        deliveryOrdersPriorityMap.set(String(order.id), order)
      })
      this.deliveryOrdersPriority = Array.from(deliveryOrdersPriorityMap.values())

      const highPriorityOrdersMap = new Map(
        this.highPriorityOrdersData.map((order) => [String(order.id), order])
      )
      simplifiedHighPriority.forEach((order) => {
        highPriorityOrdersMap.set(String(order.id), order)
      })
      this.highPriorityOrdersData = Array.from(highPriorityOrdersMap.values())

      const highPriorityIds = simplifiedHighPriority.map((order) => order.id)
      this.highPriorityOrdersIds = Array.from(
        new Set([...this.highPriorityOrdersIds, ...highPriorityIds])
      )
      this.orderReoptimizeIds = Array.from(
        new Set([...this.orderReoptimizeIds, ...deliveryOrders.map((order) => order.id)])
      )

      this.isChangedPriority = false
    },
    async getVehicleSetting() {
      const params = {
        page: 1,
        limit: 10,
      }
      const res = await RO.getSettingVehicle(this.id)(params)

      if (res.errors) return

      const vehicles = res.data

      this.vehicleReoptimizeIds = vehicles.ids
    },
  },
}
</script>
